// React Router v7 imports
import { RouterProvider, createBrowserRouter, Outlet } from 'react-router-dom'
import { fetchBreadcrumbsLabels } from 'Common/Breadcrumbs/Breadcrumbs.data'

// Debug: Ensure routes file is loaded
console.log('🔧 Routes file loaded at:', new Date().toISOString())
import { Shell } from 'Common/Layout/Layout'
import { TenantListPage } from 'Features/Tenant/TenantList.page'
import { PlannedVolumePage } from 'Features/PlannedVolume/PlannedVolume.page'
import { WorkCentreSummary } from 'Features/WorkCentreSummary/WorkCentreSummary.page'
import { EditCapacityPage } from 'Features/EditCapacity/EditCapacity.page'
import { OperationalPlannerPage } from 'Features/OperationalPlanner/OperationalPlanner.page'
import { CreateOperationalPlanPage } from 'Features/OperationalPlanner/CreateOperationalPlan.page'
import { CreateOperationalPlanV2Page } from 'Features/OperationalPlannerV2/CreateOperationalPlanV2.page'
import { ManageVariantsPage } from 'Features/OperationalPlanner/ManageVariants.page'
import { EditSettingsPage } from 'Features/EditSettings/EditSettings.page'
import { NewWorkCentreDashboardPage } from 'Features/NewDashboard/NewWorkCentreDashboard.page'
import { CreateScenarioPlanPage } from 'Features/CreateScenarioPlan/CreateScenarioPlan.page'
import { AlternativeWorkcentersPage } from 'Features/AlternativeWorkcenters/AlternativeWorkcenters.page'
import { SystemOfRecordsPage } from 'Features/SystemOfRecords/SystemOfRecords.page'
import { SORWorkcentreSummaryPage } from 'Features/SORWorkCentreSummary/SORWorkCentreSummary.page'
import { DataQualityCheckPage } from 'Features/DataQualityCheck/DataQualityCheck.Page'
import { EditProductionPage } from 'Features/EditProduction/EditProduction.page'
import { SourceSiteDashboardPage } from 'Features/SourceSiteDashboard/SourceSiteDashboard.page'
import { AdherenceReportPage } from 'Features/AdherenceReport/AdherenceReport.Page'
import { ReportsPage } from 'Features/Reports/Reports.Page'
import { WarehouseInboundReportPage } from 'Features/WarehouseInboundReport/WarehouseInboundReport.Page'

// React Router v7 configuration with breadcrumbs
const createRouter = () =>
  createBrowserRouter([
    {
      path: '/',
      element: <Shell />,
      handle: {
        crumb: () => 'Home',
      },
      children: [
        {
          path: 'tenants',
          element: <TenantListPage />,
          handle: {
            crumb: () => 'Tenants',
          },
        },
        {
          path: 'select-tenant',
          element: <TenantListPage autoRedirect={false} />,
          handle: {
            crumb: () => 'Select tenant',
          },
        },
        {
          index: true,
          element: <OperationalPlannerPage />,
          handle: {
            crumb: () => 'Home',
          },
        },
        {
          path: 'operational-plan',
          element: <Outlet />,
          handle: {
            crumb: () => 'Operational plans',
          },
          children: [
            {
              index: true,
              element: <OperationalPlannerPage />,
            },
            {
              path: ':monday',
              element: <SORWorkcentreSummaryPage />,
              // ✅ REMOVED UNNECESSARY LOADER - Just pass params directly
              handle: {
                crumb: (match: any) => {
                  // Access params from match object for compatibility with use-react-router-breadcrumbs
                  return match.params?.monday || 'Week'
                },
              },
            },
          ],
        },
        {
          path: 'scenario-plan',
          element: <Outlet />,
          handle: {
            crumb: () => 'Scenario plans',
          },
          children: [
            {
              index: true,
              element: <OperationalPlannerPage />,
            },
            {
              path: 'create-scenario-plan',
              element: <CreateScenarioPlanPage />,
              handle: {
                crumb: () => 'Create scenario plan',
              },
            },
          ],
        },
        {
          path: 'create-plan',
          element: <Outlet />,
          handle: {
            crumb: () => 'Create plan',
          },
          children: [
            {
              index: true,
              element: <CreateOperationalPlanPage />,
            },
            {
              path: 'manage-variants',
              element: <ManageVariantsPage />,
              handle: {
                crumb: () => 'Manage variants',
              },
            },
          ],
        },
        {
          path: 'create-plan-v2',
          element: <Outlet />,
          handle: {
            crumb: () => 'Create plan v2',
          },
          children: [
            {
              index: true,
              element: <CreateOperationalPlanV2Page />,
            },
            {
              path: 'manage-variants',
              element: <ManageVariantsPage />,
              handle: {
                crumb: () => 'Manage variants',
              },
            },
          ],
        },
        {
          path: 'plan',
          element: <Outlet />,
          children: [
            {
              path: ':planId',
              element: <Outlet />,
              loader: async ({ params }) => {
                try {
                  console.log('📡 About to call fetchBreadcrumbsLabels API...')
                  const data = await fetchBreadcrumbsLabels({
                    plan_id: params.planId,
                  })
                  const planName = data.data?.[params.planId!] || null
                  return { planName }
                } catch (error) {
                  return { planName: null }
                }
              },

              handle: {
                crumb: (match: any) => {
                  // Use loader data if available, fallback to planId
                  const planName = match.data?.planName
                  return planName || `Plan ${match.params?.planId || 'Unknown'}`
                },
              },
              children: [
                {
                  index: true,
                  element: <WorkCentreSummary />,
                },
                {
                  path: 'workcentres',
                  element: <SourceSiteDashboardPage />,
                  handle: {
                    crumb: () => 'Workcentres dashboard',
                  },
                },
                {
                  path: 'edit',
                  element: <EditSettingsPage />,
                  handle: {
                    crumb: () => 'Edit plan',
                  },
                },
                {
                  path: 'sourcesite/:sourcesiteId/workcentre/:workcentreId',
                  element: <Outlet />,
                  loader: async ({ params }) => {
                    try {
                      const data = await fetchBreadcrumbsLabels({
                        workcentre_id: params.workcentreId,
                        plan_id: params.planId,
                      })
                      return {
                        workcentreName:
                          data.data?.[params.workcentreId!] || null,
                      }
                    } catch (error) {
                      console.error('Error fetching breadcrumbs:', error)
                      return { workcentreName: null }
                    }
                  },
                  handle: {
                    crumb: (match: any) => {
                      // Access params from match object for compatibility with use-react-router-breadcrumbs
                      const workcentreName = match.data?.workcentreName
                      if (workcentreName) {
                        return workcentreName
                      }
                      return `Workcentre ${match.params?.workcentreId || 'Unknown'}`
                    },
                  },
                  children: [
                    {
                      index: true,
                      element: <NewWorkCentreDashboardPage />,
                    },
                    {
                      path: 'edit-capacity',
                      element: <EditCapacityPage />,
                      handle: {
                        crumb: () => 'Edit capacity',
                      },
                    },
                    {
                      path: 'alternative-workcentres',
                      element: <AlternativeWorkcentersPage />,
                      handle: {
                        crumb: () => 'Alternative Workcentres',
                      },
                    },
                    {
                      path: 'edit-production',
                      element: <EditProductionPage />,
                      handle: {
                        crumb: () => 'Edit production',
                      },
                    },
                    {
                      path: 'edit-plan-volume',
                      element: <PlannedVolumePage />,
                      handle: {
                        crumb: () => 'Edit plan volume',
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: 'system-of-records',
          element: <SystemOfRecordsPage />,
          handle: {
            crumb: () => 'System of records',
          },
        },
        {
          path: 'quality-check',
          element: <DataQualityCheckPage />,
          handle: {
            crumb: () => 'Data quality check',
          },
        },
        {
          path: 'reports',
          element: <Outlet />,
          handle: {
            crumb: () => 'Reports',
          },
          children: [
            {
              index: true,
              element: <ReportsPage />,
            },
            {
              path: 'adherence-reports',
              element: <AdherenceReportPage />,
              handle: {
                crumb: () => 'Adherence reports',
              },
            },
            {
              path: 'warehouse-inbound-report',
              element: <WarehouseInboundReportPage />,
              handle: {
                crumb: () => 'Warehouse Inbound Forecast Report',
              },
            },
          ],
        },
      ],
    },
  ])

// ✅ CREATE ROUTER OUTSIDE COMPONENT - Prevents recreation on every render
const router = createRouter()

export const AppRoutes = () => {
  return <RouterProvider router={router} />
}
